import InputShortText from "../InputShortText/InputShortText";
import TextArea from "../TextArea/TextArea";
import { useTranslation } from "react-i18next";
import SectionTitle from "../SectionTitle/SectionTitle";
import { Meeting } from "../../api/MeetingsAPI";
import MeetingsAPI from "../../api/MeetingsAPI";
import SelectInput from "../SelectInput/SelectInput";
import CONFIG from "../../config/config";
import { MeetingType, meetingTypeToTranslationKey } from "../../shared/meetingTypeToTranslationKey";
import { shouldShowReminderCheckbox } from "../../shared/meetingReminderUtils";
import CustomDatePicker from "../CustomDatePicker/CustomDatePicker";
import { useFormValidation } from "../../hooks/useFormValidation";
import { isFutureDate, isValidEmail, isValidPhone, isValidText } from "../../shared/validation";
import React, { useState, useEffect } from "react";
import useSessionStorage from "../../hooks/useSessionStorage";
import IconButton from "../IconButton/IconButton";
import { Icon } from "../Icon/Icon";
import copy from "../../assets/images/copy.svg";
import cut from "../../assets/images/cut.svg";
import paste from "../../assets/images/paste.svg";
import Tooltip from "../Tooltip/Tooltip";
import { addMinutes } from "date-fns";
import { ResourceAction, ResourceActionType } from "../../shared/resourceAction.type";
import { toastService } from "../../services/ToastService";
import SmallCheckBox from "../SmallCheckBox/SmallCheckBox";
import { PermissionFlags } from "../PermissionFlags/PermissionFlags";
import PhoneNumberInput from "../PhoneNumberInput/PhoneNumberInput";
import CustomerInput from "../CustomerInput/CustomerInput";
import { Customer } from "../../api/CustomersAPI";
import { UserX, UserCheck, ExternalLink } from "lucide-react";
import CustomerForm from "../CustomerForm/CustomerForm";
import Toggle from "../Toggle/Toggle";
import ExpandableSection from "../ExpandableSection/ExpandableSection";
import PlainTextButton from "../PlainTextButton/PlainTextButton";
import { TabV2, TabsList, TabsTrigger, TabsContent } from "../TabV2/TabV2";
import PendingBookingDetails from "../PendingBookingDetails/PendingBookingDetails";
import { PendingBookingDto } from "../../api/MeetingsAPI";
import ActionLinkButton from "../ActionLinkButton/ActionLinkButton";

type Props = {
  meeting: Meeting | null,
  onMeetingChange?: (value: Meeting) => void,
  isDisabled?: boolean,
  actionButton?: JSX.Element,
  timeZone?: string,
  onPaymentStatusChange?: (id: number, paid: boolean) => Promise<void>;
};

const getInitialState = (meeting: Meeting | null) => {
  if (meeting) {
    return {
      customerName: isValidText(meeting.customerName, 2),
    };
  }
};

function MeetingForm({ meeting, onMeetingChange = () => null, isDisabled = false, actionButton, timeZone, onPaymentStatusChange }: Props) {

  const { t } = useTranslation();
  const requiredFields: string[] = [];
  const initialState = getInitialState(meeting);
  const { updateFieldValidation, isFormValid } = useFormValidation(requiredFields, initialState);
  const [copiedMeeting, setCopiedMeeting] = useSessionStorage<ResourceAction<Meeting> | null>('copiedResource', null);
  const [isTogglingNoShow, setIsTogglingNoShow] = useState(false);
  const [customerDetails, setCustomerDetails] = useState<Customer | null>(null);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
  const [showAdditionalSettings, setShowAdditionalSettings] = useState(false);
  const [bookingDetails, setBookingDetails] = useState<PendingBookingDto | null>(null);
  const [isLoadingBookingDetails, setIsLoadingBookingDetails] = useState(false);

  const registerAction = (action: ResourceActionType) => {
    displayMessageFeedback(action);
    setCopiedMeeting({ action: action, resource: meeting! });
  }

  const displayMessageFeedback = (action: ResourceActionType) => {
    if (action === "copy") {
      toastService.showToast(t("global.already_copy"));
    } else {
      toastService.showToast(t("global.already_cut"));
    }
  }

  const handleCut = () => {
    if (meeting) registerAction('cut');
  };

  const handleCopy = () => {
    if (meeting) registerAction('copy');
  };

  const handleMeetingChange = (updatedMeeting: Meeting) => {
    onMeetingChange(updatedMeeting);
    updateFieldValidation(
      'customerName',
      !updatedMeeting.customerName || isValidText(updatedMeeting.customerName, 2)
    );
  };  

  const sanitizeMeetingForPaste = (resource: Meeting): Meeting => {
    const { reservationId, bookedMeetingType, inboundChannel, ...rest } = resource as any;
    return rest as Meeting;
  };

  const handlePaste = () => {
    if (copiedMeeting) {
      if (copiedMeeting.action === 'copy') {
        const sanitized = sanitizeMeetingForPaste(copiedMeeting.resource);
        handleMeetingChange({
          ...sanitized,
          id: meeting?.id,
          startTime: meeting?.startTime,
          endTime: meeting?.endTime,
        } as Meeting);
      } else if (copiedMeeting.action === 'cut') {
        handleMeetingChange({
          ...copiedMeeting.resource,
          startTime: meeting?.startTime,
          endTime: meeting?.endTime,
        } as Meeting);
      } else {
        console.error("Could not execute action on resource!");
      }
    }
  };

  const disabledActionButton = actionButton
    ? React.cloneElement(
        actionButton,
        {},
        React.Children.map(actionButton.props.children, (child) => {
          return child ? React.cloneElement(child, { isDisabled: !isFormValid() }) : null;
        })
      )
    : null;

  const fillCustomerData = (customer: Customer) => {
    onMeetingChange(
      {...meeting, 
        customerName: customer.name, 
        customerSurname: customer.surname,
        customer: customer,
        phone: customer?.phone,
        email: customer?.email
      } as Meeting);
  }

  const handleToggleNoShow = async () => {
    if (!meeting?.id || isTogglingNoShow) return;
    
    setIsTogglingNoShow(true);
    try {
      const response = await MeetingsAPI.markNoShow(meeting.id);
      
      onMeetingChange({
        ...meeting,
        noShow: response.data.noShow
      } as Meeting);
      
      toastService.showToast(
        response.data.noShow 
          ? t("calendar.meeting.form.marked_noshow", "Meeting marked as no-show")
          : t("calendar.meeting.form.unmarked_noshow", "Meeting unmarked as no-show")
      );
    } catch (error) {
      console.error('Failed to toggle no-show status:', error);
      toastService.showToast(t("toast.errors.generic", "An error occurred"));
    } finally {
      setIsTogglingNoShow(false);
    }
  };

  const fetchCustomerDetails = async () => {
    if (!meeting?.customerId) return;
    
    if (customerDetails) return;
    
    setIsLoadingCustomer(true);
    try {
      const { default: CustomersAPI } = await import('../../api/CustomersAPI');
      const response = await CustomersAPI.getById(meeting.customerId.toString());
      setCustomerDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch customer details:', error);
      toastService.showToast(t("toast.errors.generic", "Failed to load customer details"));
    } finally {
      setIsLoadingCustomer(false);
    }
  };

  const fetchBookingDetails = async () => {
    if (!meeting?.reservationId || !meeting?.id) return;
    
    if (bookingDetails) return;
    
    setIsLoadingBookingDetails(true);
    try {
      const response = await MeetingsAPI.getBookingByReservationId(meeting.id.toString(), meeting.reservationId);
      setBookingDetails(response.data);
    } catch (error) {
      console.error('Error fetching booking details:', error);
      toastService.showToast(t("bookings.details.fetch_error"));
    } finally {
      setIsLoadingBookingDetails(false);
    }
  };

  const handlePaymentStatusChange = async (id: number, paid: boolean) => {
    if (onPaymentStatusChange) {
      await onPaymentStatusChange(id, paid);
      if (meeting?.reservationId && meeting?.id) {
        await fetchBookingDetails();
      }
    }
  };

  useEffect(() => {
    setCustomerDetails(null);
    setBookingDetails(null);
  }, [meeting?.id, meeting?.customerId, meeting?.reservationId]);

  const hasReservationDetails = !!meeting?.reservationId;
  const hasCustomerDetails = !!meeting?.customerId;
  
  const shouldShowReservationTab = !CONFIG.MEETINGS.RESERVATION_TAB_EXCLUDED_TYPES.includes(meeting?.type || '');
  const tabCount = 1 + (shouldShowReservationTab ? 1 : 0) + (hasCustomerDetails ? 1 : 0);
  const defaultTab = "meeting-form";

  return (
    <>
      {tabCount > 1 ? (
        <TabV2 
          defaultValue={defaultTab} 
          className="w-full"
          onValueChange={(value) => {
            if (value === "reservation-details") {
              fetchBookingDetails();
            } else if (value === "customer-details") {
              fetchCustomerDetails();
            }
          }}
        >
          <TabsList className={tabCount === 2 ? "grid w-full grid-cols-2 py-[15px]" : tabCount === 3 ? "grid w-full grid-cols-3 py-[15px]" : "grid w-full grid-cols-1 py-[15px]"}>
            <TabsTrigger value="meeting-form">
              {t("calendar.meeting.form.tabs.appointment") || "Appointment"}
            </TabsTrigger>
            {shouldShowReservationTab && (
              <TabsTrigger value="reservation-details">
                {t("calendar.meeting.form.tabs.booking") || "Booking"}
              </TabsTrigger>
            )}
            {hasCustomerDetails && (
              <TabsTrigger value="customer-details">
                {t("calendar.meeting.form.tabs.customer_profile") || "Customer profile"}
              </TabsTrigger>
            )}
          </TabsList>
          
          <div className="mb-4"></div>
          
          <TabsContent value="meeting-form" className="pb-24 md:pb-0">
            <MeetingFormContent 
              meeting={meeting}
              onMeetingChange={handleMeetingChange}
              isDisabled={isDisabled}
              timeZone={timeZone}
              showAdditionalSettings={showAdditionalSettings}
              setShowAdditionalSettings={setShowAdditionalSettings}
              fillCustomerData={fillCustomerData}
              updateFieldValidation={updateFieldValidation}
              copiedMeeting={copiedMeeting}
              handlePaste={handlePaste}
              handleCut={handleCut}
              handleCopy={handleCopy}
              handleToggleNoShow={handleToggleNoShow}
              isTogglingNoShow={isTogglingNoShow}
            />
          </TabsContent>
          
          {shouldShowReservationTab && (
            <TabsContent value="reservation-details">
              <div className="max-h-[60vh] overflow-y-auto">
                {hasReservationDetails ? (
                  <PendingBookingDetails 
                    booking={bookingDetails} 
                    isLoading={isLoadingBookingDetails}
                    onPaymentStatusChange={handlePaymentStatusChange}
                  />
                ) : (
                  <div className="flex items-center justify-center py-16">
                    <p className="text-gray-600">{t("calendar.meeting.form.no_reservation") || "No reservation associated to this appointment"}</p>
                  </div>
                )}
              </div>
            </TabsContent>
          )}
          
          {hasCustomerDetails && (
            <TabsContent value="customer-details">
              <div className="max-h-[60vh] overflow-y-auto">
                <CustomerProfileContent
                  isLoadingCustomer={isLoadingCustomer}
                  customerDetails={customerDetails}
                />
              </div>
            </TabsContent>
          )}
        </TabV2>
      ) : (
        <div className="pb-24 md:pb-0">
          <MeetingFormContent 
            meeting={meeting}
            onMeetingChange={handleMeetingChange}
            isDisabled={isDisabled}
            timeZone={timeZone}
            showAdditionalSettings={showAdditionalSettings}
            setShowAdditionalSettings={setShowAdditionalSettings}
            fillCustomerData={fillCustomerData}
            updateFieldValidation={updateFieldValidation}
            copiedMeeting={copiedMeeting}
            handlePaste={handlePaste}
            handleCut={handleCut}
            handleCopy={handleCopy}
            handleToggleNoShow={handleToggleNoShow}
            isTogglingNoShow={isTogglingNoShow}
          />
        </div>
      )}
      
      {/* Mobile-only */}
      {disabledActionButton && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-10">
          <div className="max-w-7xl mx-auto">
            {disabledActionButton}
          </div>
        </div>
      )}
      
      {/* Desktop action button */}
      <div className="hidden md:block">
        {disabledActionButton}
      </div>
    </>
  );
}

type MeetingFormContentProps = {
  meeting: Meeting | null;
  onMeetingChange: (value: Meeting) => void;
  isDisabled: boolean;
  timeZone?: string;
  showAdditionalSettings: boolean;
  setShowAdditionalSettings: React.Dispatch<React.SetStateAction<boolean>>;
  fillCustomerData: (customer: Customer) => void;
  updateFieldValidation: (field: string, isValid: boolean) => void;
  copiedMeeting: ResourceAction<Meeting> | null;
  handlePaste: () => void;
  handleCut: () => void;
  handleCopy: () => void;
  handleToggleNoShow: () => void;
  isTogglingNoShow: boolean;
};

function MeetingFormContent({
  meeting,
  onMeetingChange,
  isDisabled,
  timeZone,
  showAdditionalSettings,
  setShowAdditionalSettings,
  fillCustomerData,
  updateFieldValidation,
  copiedMeeting,
  handlePaste,
  handleCut,
  handleCopy,
  handleToggleNoShow,
  isTogglingNoShow
}: MeetingFormContentProps) {
  const { t } = useTranslation();
  
  const updateMeetingTimes = (start: Date) => {
    const end = addMinutes(start, 60);
    onMeetingChange({...meeting, startTime: start.toISOString(), endTime: end.toISOString() } as Meeting);
  };

  return (
    <>
      <PermissionFlags requiredFlags={['create-meetings']}>
        <div className="flex justify-end mb-4">
          {(!isDisabled && copiedMeeting) && (
            <Tooltip content={t("global.paste")} position="bottom">
              <IconButton onClick={handlePaste} disabled={isDisabled || !copiedMeeting}>
                <Icon url={paste} cssStyles="w-5"/>
              </IconButton>
            </Tooltip>
          )} 
          <Tooltip content={t("global.cut")} position="bottom">
            <IconButton onClick={handleCut}>
              <Icon url={cut} cssStyles="w-5"/>
            </IconButton>
          </Tooltip>
          <Tooltip content={t("global.copy")} position="bottom">
            <IconButton onClick={handleCopy}>
              <Icon url={copy} cssStyles="w-5"/>
            </IconButton>
          </Tooltip>
          {meeting?.id && (
            <Tooltip content={meeting.noShow 
              ? t("calendar.meeting.form.unmark_noshow", "Unmark as No-Show")
              : t("calendar.meeting.form.mark_noshow", "Mark as No-Show")
            } position="bottom">
              <div className={meeting.noShow ? "text-red-600 hover:text-red-700" : "text-gray-600 hover:text-gray-700"}>
                <IconButton 
                  onClick={handleToggleNoShow}
                  disabled={isDisabled || isTogglingNoShow}
                >
                  {meeting.noShow ? <UserX className="w-5 h-5" /> : <UserCheck className="w-5 h-5" />}
                </IconButton>
              </div>
            </Tooltip>
          )}
        </div>
      </PermissionFlags>
      <div className="flex flex-wrap mb-2">
        <SelectInput values={CONFIG.MEETINGS.STATUS.map(e => { return { value: e, readableValue: t(meetingTypeToTranslationKey(e as MeetingType)) } })}
          onChange={e => onMeetingChange({ ...meeting, type: e.currentTarget.value } as Meeting)}
          label={"calendar.meeting.form.type"}
          defaultValue={meeting?.type || 'VISIT'}
          isDisabled={isDisabled}
        />
      </div>
      <div className="flex flex-wrap space-x-2">
        <div className="flex-1">
          <CustomerInput
            onCustomerSelect={c => fillCustomerData(c)}
            onValidationChange={(isValid) => updateFieldValidation('customerName', isValid)}
            label={"calendar.meeting.form.name"}
            onChange={e => onMeetingChange({ ...meeting, customerName: e.currentTarget.value } as Meeting)}
            errorMessage={"errors.too_short"}
            defaultValue={meeting?.customer?.name || meeting?.customerName || ''}
            isDisabled={isDisabled}
            // validationFn={(value) => !!value && isValidText(value, 2)}
          />
        </div>
        { !meeting?.customer && 
          <InputShortText
            label={"calendar.meeting.form.surname"}
            validationFn={(value) => !!value && isValidText(value, 2)}
            onChange={e => onMeetingChange({ ...meeting, customerSurname: e.currentTarget.value } as Meeting)}
            errorMessage={"errors.too_short"}
            defaultValue={meeting?.customer?.surname || meeting?.customerSurname || ''}
            isDisabled={isDisabled}
          />
        }
      </div>
      <br/>
      <div className="flex flex-wrap space-x-5">
        <CustomDatePicker
          validationFn={(value) => !!value && isFutureDate(value)}
          errorMessage={"errors.past_date"}
          dateFormat="dd/MM/yyyy HH:mm"
          label="calendar.meeting.form.start"
          value={meeting?.startTime}
          onChange={c => updateMeetingTimes(c)}
          showTimeSelect
          isDisabled={isDisabled}
          timeZone={timeZone}
        />
        <CustomDatePicker
          dateFormat="dd/MM/yyyy HH:mm"
          label="calendar.meeting.form.end"
          validationFn={(value) => !!value && isFutureDate(value)}
          errorMessage={"errors.past_date"}
          value={meeting?.endTime}
          onChange={c => onMeetingChange({...meeting, endTime: c!.toISOString() } as Meeting)}
          showTimeSelect
          isDisabled={isDisabled}
          timeZone={timeZone}
        />
      </div>
      <br/>
      <SectionTitle text={t("admin.form.other_data")} />
      <div className="flex flex-wrap space-x-5">
        <CustomDatePicker
          value={meeting?.weddingDate}
          onChange={e => onMeetingChange({ ...meeting, weddingDate: e.toISOString() } as Meeting)}
          label={"calendar.meeting.form.wedding_date"}
          dateFormat="LL/yyyy"
          isDisabled={isDisabled}
          timeZone={timeZone}
        />
        <InputShortText
          label={"calendar.meeting.form.email"}
          validationFn={(value) => !!value && isValidEmail(value)}
          onChange={e => onMeetingChange({ ...meeting, email: e.currentTarget.value } as Meeting)}
          errorMessage={"errors.invalid_email"}
          defaultValue={meeting?.customer?.email || meeting?.email || ''}
          isDisabled={isDisabled}
        />
      </div>
      <div>
        <div className="flex flex-wrap space-x-5">
          <PhoneNumberInput 
            validationFn={(value) => !!value && isValidPhone(value)} 
            label={"calendar.meeting.form.phone"}
            onChange={e => onMeetingChange({ ...meeting, phone: e } as Meeting)}
            defaultValue={meeting?.customer?.phone || meeting?.phone || ''}
            isDisabled={isDisabled}
          />
        </div>
        { meeting?.type && shouldShowReminderCheckbox(meeting.type) && 
        <div className="flex flex-wrap">
          <SmallCheckBox
            label={"calendar.meeting.form.phone.reminder"}
            isDisabled={isDisabled}
            defaultValue={meeting?.reminder}
            onChange={e => 
              onMeetingChange({ ...meeting, reminder: Boolean(e.currentTarget.checked) } as Meeting)}
          />
        </div>
        }
        <TextArea
          label={"calendar.meeting.form.notes"}
          validationFn={(value) => !value || (!!value &&  isValidText(value, 0, 1000))}
          onChange={e => {
            const trimmedValue = e.currentTarget.value.trim();
            onMeetingChange({ ...meeting, notes: trimmedValue } as Meeting);
          }}
          errorMessage={"errors.too_long"}
          defaultValue={meeting?.notes || ''}
          isDisabled={isDisabled}
          maxLength={1000}
        />
        <div className="my-4">
          <ExpandableSection
            defaultState={showAdditionalSettings}
            expandedContent={
              <div className="space-y-4 p-3 border rounded-lg bg-gray-50">
                <div className="text-sm text-muted-foreground">
                  {t("calendar.meeting.form.more_settings.hint") || "Advanced settings for this meeting."}
                </div>
                <div className="flex items-start justify-between">
                  <div className="pr-4">
                    <div className="text-sm font-medium">
                      {t("calendar.meeting.form.exclusive") || "Exclusive"}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {t("calendar.meeting.form.exclusive.hint") || "When enabled, no other appointments can overlap this time."}
                    </div>
                  </div>
                  <Toggle
                    label={t("calendar.meeting.form.exclusive") || "Exclusive"}
                    defaultValue={Boolean(meeting?.exclusive)}
                    isDisabled={isDisabled}
                    onChange={(value: boolean) => onMeetingChange({ ...meeting, exclusive: value } as Meeting)}
                  />
                </div>
              </div>
            }
            collapsedContent={
              <div className="inline-flex">
                <PlainTextButton 
                  text={t("calendar.meeting.form.more_settings") || "More settings"}
                  onClick={() => setShowAdditionalSettings(prev => !prev)}
                  actionName="Expand Meeting More Settings"
                />
              </div>
            }
          />
        </div>
      </div>
    </>
  );
}

type CustomerProfileContentProps = {
  isLoadingCustomer: boolean;
  customerDetails: Customer | null;
};

function CustomerProfileContent({ isLoadingCustomer, customerDetails }: CustomerProfileContentProps) {
  const { t } = useTranslation();

  const handleEditInNewTab = () => {
    if (customerDetails?.id) {
      // window.open(`/customers?customerId=${customerDetails.id}`, '_blank', 'noopener,noreferrer');
      window.open('/customers', '_blank', 'noopener,noreferrer');
    } else {
      window.open('/customers', '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <div className="mb-4 flex justify-end">
        <ActionLinkButton
          text={t("calendar.meeting.form.edit_in_different_tab") || "Edit in a different tab"}
          onClick={handleEditInNewTab}
          actionName="Edit Customer in New Tab"
          icon={<ExternalLink className="h-4 w-4" />}
        />
      </div>
      {isLoadingCustomer ? (
        <div className="flex flex-col items-center justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">{t("customers.details.loading")}</p>
        </div>
      ) : customerDetails ? (
        <CustomerForm
          customer={customerDetails}
          isDisabled={true}
        />
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-600">{t("customers.details.not_found") || "Customer details not available"}</p>
        </div>
      )}
    </>
  );
}

export default MeetingForm;
